import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  TrendingUp, TrendingDown, ArrowRightLeft, Calendar as CalendarIcon,
  RefreshCw, Minus, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { FilterPanel } from '@/components/FilterPanel';
import { ExportDropdown } from '@/components/ExportDropdown';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import { format, subMonths, subQuarters, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter } from 'date-fns';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { TrustScoreGate } from '@/components/TrustScoreGate';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

const PRESETS = [
  { key: 'mom', label: 'MoM', description: 'Month over Month' },
  { key: 'qoq', label: 'QoQ', description: 'Quarter over Quarter' },
  { key: 'yoy', label: 'YoY', description: 'Year over Year' },
  { key: 'custom', label: 'Custom', description: 'Select custom ranges' },
];

function computePresetDates(preset, fiscalStartMonth = 1, latestDate = null) {
  const now = latestDate || new Date();

  // Helper: get fiscal year start/end for a given reference date
  function fiscalYearRange(refDate) {
    const y = refDate.getFullYear();
    const m = refDate.getMonth() + 1; // 1-12
    let fyStart, fyEnd;
    if (fiscalStartMonth === 1) {
      fyStart = new Date(y, 0, 1);
      fyEnd = new Date(y, 11, 31);
    } else if (m >= fiscalStartMonth) {
      fyStart = new Date(y, fiscalStartMonth - 1, 1);
      fyEnd = new Date(y + 1, fiscalStartMonth - 1, 0);
    } else {
      fyStart = new Date(y - 1, fiscalStartMonth - 1, 1);
      fyEnd = new Date(y, fiscalStartMonth - 1, 0);
    }
    return { fyStart, fyEnd };
  }

  switch (preset) {
    case 'mom': {
      const baseStart = startOfMonth(subMonths(now, 1));
      const baseEnd = endOfMonth(subMonths(now, 1));
      const compStart = startOfMonth(subMonths(now, 2));
      const compEnd = endOfMonth(subMonths(now, 2));
      return { baseStart, baseEnd, compStart, compEnd };
    }
    case 'qoq': {
      const baseStart = startOfQuarter(subQuarters(now, 1));
      const baseEnd = endOfQuarter(subQuarters(now, 1));
      const compStart = startOfQuarter(subQuarters(now, 2));
      const compEnd = endOfQuarter(subQuarters(now, 2));
      return { baseStart, baseEnd, compStart, compEnd };
    }
    case 'yoy': {
      // Most recent complete fiscal year as base, previous FY as comparison
      const currentFY = fiscalYearRange(now);
      const prevFYRef = new Date(currentFY.fyStart);
      prevFYRef.setFullYear(prevFYRef.getFullYear() - 1);
      const prevFY = fiscalYearRange(prevFYRef);
      const olderFYRef = new Date(prevFY.fyStart);
      olderFYRef.setFullYear(olderFYRef.getFullYear() - 1);
      const olderFY = fiscalYearRange(olderFYRef);
      return {
        baseStart: prevFY.fyStart,
        baseEnd: prevFY.fyEnd,
        compStart: olderFY.fyStart,
        compEnd: olderFY.fyEnd,
      };
    }
    default:
      return null;
  }
}

function formatDate(d) {
  return d ? format(d, 'yyyy-MM-dd') : null;
}

function DeltaBadge({ value, suffix = '%' }) {
  if (value == null || isNaN(value)) return <span className="text-muted-foreground">-</span>;
  const positive = value > 0;
  const negative = value < 0;
  return (
    <span className={cn(
      'inline-flex items-center gap-0.5 font-semibold text-sm',
      positive && 'text-destructive',
      negative && 'text-secondary',
      !positive && !negative && 'text-muted-foreground',
    )}>
      {positive ? <ArrowUpRight className="w-3.5 h-3.5" /> : negative ? <ArrowDownRight className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
      {positive ? '+' : ''}{value?.toFixed(1)}{suffix}
    </span>
  );
}

function DatePickerBtn({ date, onSelect, label, testId }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn('w-full h-9 justify-start text-left font-normal text-xs', !date && 'text-muted-foreground')}
            data-testid={testId}
          >
            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
            {date ? format(date, 'dd MMM yyyy') : 'Select'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={date} onSelect={onSelect} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function ComparativeAnalysis() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState('yoy');
  const [baseStart, setBaseStart] = useState(null);
  const [baseEnd, setBaseEnd] = useState(null);
  const [compStart, setCompStart] = useState(null);
  const [compEnd, setCompEnd] = useState(null);
  const [fiscalStartMonth, setFiscalStartMonth] = useState(1);
  const [latestDataDate, setLatestDataDate] = useState(null);
  const { filters: additionalFilters, setFilters: setAdditionalFilters } = useGlobalFilters();

  // Fetch org fiscal settings + data date range, then apply default preset
  useEffect(() => {
    async function init() {
      let startMonth = 1;
      let dataDate = null;
      try {
        const [fiscalRes, rangeRes] = await Promise.all([
          api.get('/settings/fiscal-calendar').catch(() => ({ data: {} })),
          api.get('/analytics/data-date-range').catch(() => ({ data: {} })),
        ]);
        startMonth = fiscalRes.data?.fiscal_year_start_month || 1;
        setFiscalStartMonth(startMonth);
        if (rangeRes.data?.max_date) {
          dataDate = new Date(rangeRes.data.max_date);
          setLatestDataDate(dataDate);
        }
      } catch {}
      // Deep-link support (e.g. Signal Intelligence "Open in analytics"):
      // explicit periods + category in the URL take precedence over the default preset
      // so the exact comparison from the signal is reproduced.
      const params = new URLSearchParams(window.location.search);
      const linkBs = params.get('base_start');
      const linkBe = params.get('base_end');
      const linkCs = params.get('comp_start');
      const linkCe = params.get('comp_end');
      const linkCat = params.get('category');
      if (linkBs && linkBe && linkCs && linkCe) {
        setPreset('custom');
        setBaseStart(new Date(linkBs));
        setBaseEnd(new Date(linkBe));
        setCompStart(new Date(linkCs));
        setCompEnd(new Date(linkCe));
        if (linkCat) setAdditionalFilters((prev) => ({ ...prev, category: linkCat }));
        return;
      }
      const dates = computePresetDates('yoy', startMonth, dataDate);
      if (dates) {
        setBaseStart(dates.baseStart);
        setBaseEnd(dates.baseEnd);
        setCompStart(dates.compStart);
        setCompEnd(dates.compEnd);
      }
    }
    init();
  }, []);

  const fetchData = useCallback(async (bs, be, cs, ce, filterObj = {}) => {
    if (!bs || !be || !cs || !ce) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('base_start', formatDate(bs));
      params.append('base_end', formatDate(be));
      params.append('comp_start', formatDate(cs));
      params.append('comp_end', formatDate(ce));
      if (filterObj.category) params.append('category', filterObj.category);
      if (filterObj.category_l2) params.append('category_l2', filterObj.category_l2);
      if (filterObj.category_l3) params.append('category_l3', filterObj.category_l3);
      if (filterObj.region) params.append('region', filterObj.region);
      if (filterObj.country) params.append('country', filterObj.country);
      if (filterObj.sbu) params.append('sbu', filterObj.sbu);
    if (filterObj.cost_center) params.append('cost_center', filterObj.cost_center);
    if (filterObj.business_unit) params.append('business_unit', filterObj.business_unit);
    if (filterObj.addressability) params.append('addressability', filterObj.addressability);
      if (filterObj.fiscal_year) params.append('fiscal_year', filterObj.fiscal_year);
      const response = await api.get(`/analytics/comparative?${params.toString()}`);
      setData(response.data);
    } catch {
      toast.error('Failed to load comparative analysis');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch when dates or filters change
  useEffect(() => {
    if (baseStart && baseEnd && compStart && compEnd) {
      fetchData(baseStart, baseEnd, compStart, compEnd, additionalFilters);
    }
  }, [baseStart, baseEnd, compStart, compEnd, additionalFilters, fetchData]);

  const handleAdditionalFiltersChange = (newFilters) => {
    setAdditionalFilters(newFilters);
  };

  const handlePresetChange = (key) => {
    setPreset(key);
    if (key !== 'custom') {
      const dates = computePresetDates(key, fiscalStartMonth, latestDataDate);
      if (dates) {
        setBaseStart(dates.baseStart);
        setBaseEnd(dates.baseEnd);
        setCompStart(dates.compStart);
        setCompEnd(dates.compEnd);
      }
    }
  };

  const baseLabel = useMemo(() => {
    if (baseStart && baseEnd) return `${format(baseStart, 'MMM yyyy')} - ${format(baseEnd, 'MMM yyyy')}`;
    return 'Base Period';
  }, [baseStart, baseEnd]);

  const compLabel = useMemo(() => {
    if (compStart && compEnd) return `${format(compStart, 'MMM yyyy')} - ${format(compEnd, 'MMM yyyy')}`;
    return 'Comparison Period';
  }, [compStart, compEnd]);

  const activeFilterCount = Object.values(additionalFilters).filter(Boolean).length;

  // --- Exports ---
  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['--- Summary ---'],
      ['', 'Base Period', 'Comparison Period', 'Change', 'Change %'],
      ['Total Spend', data.summary?.base_spend, data.summary?.comp_spend, data.summary?.delta_spend, `${data.summary?.delta_pct}%`],
      [],
      ['--- Category Comparison ---'],
      ['Category', 'Base Spend', 'Comparison Spend', 'Delta', 'Delta %'],
      ...(data.by_category || []).map(c => [c.category, c.base_spend, c.comp_spend, c.delta, `${c.delta_pct}%`]),
      [],
      ['--- Entity (Country Code) Comparison ---'],
      ['Entity (Country Code)', 'Base Spend', 'Comparison Spend', 'Delta', 'Delta %'],
      ...(data.by_entity || []).map(e => [e.entity, e.base_spend, e.comp_spend, e.delta, `${e.delta_pct}%`]),
      [],
      ['--- SBU Comparison ---'],
      ['SBU', 'Base Spend', 'Comparison Spend', 'Delta', 'Delta %'],
      ...(data.by_sbu || []).map(s => [s.sbu, s.base_spend, s.comp_spend, s.delta, `${s.delta_pct}%`]),
    ];
    downloadCSV(rows, 'comparative_analysis');
  };

  const exportPDF = () => {
    if (!data) return;
    buildPDF([
      { title: `Summary (${baseLabel} vs ${compLabel})`, headers: ['Metric', 'Base', 'Comparison', 'Change', 'Change %'], rows: [['Total Spend', `${cs}${data.summary?.base_spend?.toLocaleString()}`, `${cs}${data.summary?.comp_spend?.toLocaleString()}`, `${cs}${data.summary?.delta_spend?.toLocaleString()}`, `${data.summary?.delta_pct}%`]] },
      { title: 'Category Comparison', headers: ['Category', 'Base', 'Comparison', 'Delta', 'Delta %'], rows: (data.by_category || []).map(c => [c.category, `${cs}${c.base_spend?.toLocaleString()}`, `${cs}${c.comp_spend?.toLocaleString()}`, `${cs}${c.delta?.toLocaleString()}`, `${c.delta_pct}%`]) },
      { title: 'Entity (Country Code) Comparison', headers: ['Entity (Country Code)', 'Base', 'Comparison', 'Delta', 'Delta %'], rows: (data.by_entity || []).map(e => [e.entity, `${cs}${e.base_spend?.toLocaleString()}`, `${cs}${e.comp_spend?.toLocaleString()}`, `${cs}${e.delta?.toLocaleString()}`, `${e.delta_pct}%`]) },
      { title: 'SBU Comparison', headers: ['SBU', 'Base', 'Comparison', 'Delta', 'Delta %'], rows: (data.by_sbu || []).map(s => [s.sbu, `${cs}${s.base_spend?.toLocaleString()}`, `${cs}${s.comp_spend?.toLocaleString()}`, `${cs}${s.delta?.toLocaleString()}`, `${s.delta_pct}%`]) },
    ], 'Comparative_Analysis');
  };

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  };

  return (
    <TrustScoreGate mode="soft" moduleLabel="Comparative Analysis">
    <div className="space-y-4" data-testid="comparative-analysis-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Comparative Analysis</h1>
          <p className="text-muted-foreground mt-1">
            Compare spend across different time periods
            {activeFilterCount > 0 && (
              <span className="ml-2 text-primary font-medium">
                ({activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="comparative" />
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(baseStart, baseEnd, compStart, compEnd, additionalFilters)}
            disabled={loading}
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <Card className="border-dashed" data-testid="period-selector">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-primary" />
            <CardTitle className="text-base">Period Selection</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Presets */}
          <div className="flex flex-wrap gap-2" data-testid="preset-buttons">
            {PRESETS.map(p => (
              <Button
                key={p.key}
                variant={preset === p.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => handlePresetChange(p.key)}
                data-testid={`preset-${p.key}`}
                className="min-w-[64px]"
              >
                {p.label}
              </Button>
            ))}
            <span className="text-xs text-muted-foreground self-center ml-2">
              {PRESETS.find(p => p.key === preset)?.description}
            </span>
          </div>

          {/* Date pickers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Base Period */}
            <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="text-xs">Base Period</Badge>
                {baseStart && baseEnd && (
                  <span className="text-xs text-muted-foreground">{baseLabel}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DatePickerBtn date={baseStart} onSelect={d => { setBaseStart(d); setPreset('custom'); }} label="Start" testId="base-start-date" />
                <DatePickerBtn date={baseEnd} onSelect={d => { setBaseEnd(d); setPreset('custom'); }} label="End" testId="base-end-date" />
              </div>
            </div>

            {/* Comparison Period */}
            <div className="p-4 rounded-lg border border-muted bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">Comparison Period</Badge>
                {compStart && compEnd && (
                  <span className="text-xs text-muted-foreground">{compLabel}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DatePickerBtn date={compStart} onSelect={d => { setCompStart(d); setPreset('custom'); }} label="Start" testId="comp-start-date" />
                <DatePickerBtn date={compEnd} onSelect={d => { setCompEnd(d); setPreset('custom'); }} label="End" testId="comp-end-date" />
              </div>
            </div>
          </div>

          {/* Additional filters */}
          <div className="pt-3 border-t border-border">
            <FilterPanel
              filters={additionalFilters}
              onFiltersChange={handleAdditionalFiltersChange}
              pageName="comparative"
              visibleFilters={['category', 'category_l2', 'category_l3', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
              compact={false}
            />
          </div>
        </CardContent>
      </Card>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {/* No data state */}
      {!loading && !data && (
        <Card className="py-12">
          <CardContent className="text-center text-muted-foreground">
            <ArrowRightLeft className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg font-medium">Select time periods to compare</p>
            <p className="text-sm mt-1">Use the presets above or pick custom date ranges</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!loading && data && (
        <>
          {/* Summary KPI Cards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card data-testid="kpi-base-spend">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  Base Period Spend
                  <HelpBubble text="Total FX-normalised spend in the earlier (benchmark) period you selected. This is the baseline every comparison below is measured against." />
                </CardTitle>
                <TrendingUp className="w-5 h-5 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold font-mono">{cs}{data.summary?.base_spend?.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">{data.summary?.base_count?.toLocaleString()} transactions</p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-comp-spend">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  Comparison Period
                  <HelpBubble text="Total FX-normalised spend in the later (comparison) period. Compare this against the Base Period to see how spend moved." />
                </CardTitle>
                <TrendingDown className="w-5 h-5 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold font-mono">{cs}{data.summary?.comp_spend?.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">{data.summary?.comp_count?.toLocaleString()} transactions</p>
              </CardContent>
            </Card>

            <Card data-testid="kpi-delta-spend">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  Absolute Change
                  <HelpBubble text="Comparison Period minus Base Period, in your base currency. Positive = spend grew; negative = spend shrank." />
                </CardTitle>
                {data.summary?.delta_spend >= 0 ? (
                  <ArrowUpRight className="w-5 h-5 text-destructive" />
                ) : (
                  <ArrowDownRight className="w-5 h-5 text-secondary" />
                )}
              </CardHeader>
              <CardContent>
                <div className={cn('text-lg font-bold font-mono', data.summary?.delta_spend >= 0 ? 'text-destructive' : 'text-secondary')}>
                  {data.summary?.delta_spend >= 0 ? '+' : ''}{cs}{Math.abs(data.summary?.delta_spend || 0)?.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-delta-pct">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  Percentage Change
                  <HelpBubble text="Absolute Change expressed as a percentage of Base Period spend. Swings &gt;10% warrant a deeper look at the By Category and By SBU tabs." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={cn('text-lg font-bold font-mono', data.summary?.delta_pct >= 0 ? 'text-destructive' : 'text-secondary')}>
                  {data.summary?.delta_pct >= 0 ? '+' : ''}{data.summary?.delta_pct?.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">vs comparison period</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Tabs */}
          <Tabs defaultValue="category" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="category" data-testid="category-tab">By Category</TabsTrigger>
              <TabsTrigger value="entity" data-testid="business-unit-tab">By Entity (Country Code)</TabsTrigger>
              <TabsTrigger value="sbu" data-testid="sbu-tab">By SBU</TabsTrigger>
              <TabsTrigger value="trend" data-testid="trend-tab">Monthly Trend</TabsTrigger>
            </TabsList>

            {/* Category Comparison */}
            <TabsContent value="category">
              <div className="space-y-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card data-testid="category-comparison-chart">
                    <CardHeader>
                      <CardTitle>Category Spend: Base vs Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={(data.by_category || []).slice(0, 15)} layout="vertical" margin={{ left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis type="number" stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${cs}${(v / 1000).toFixed(0)}k`} />
                          <YAxis dataKey="category" type="category" width={140} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={tooltipStyle} formatter={(val) => [`${cs}${val?.toLocaleString()}`, '']} />
                          <Legend />
                          <Bar dataKey="base_spend" name={baseLabel} fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} barSize={14} />
                          <Bar dataKey="comp_spend" name={compLabel} fill="hsl(var(--muted-foreground))" radius={[0, 4, 4, 0]} barSize={14} opacity={0.5} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <Card data-testid="category-comparison-table">
                    <CardHeader><CardTitle>Category Details</CardTitle></CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Base Spend</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Comparison</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Delta</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            <>{(data.by_category || []).map(row => (
                              <tr key={row.category} className="border-b border-border hover:bg-muted/50 transition-colors">
                                <td className="py-1.5 px-3 font-medium text-sm">{row.category}</td>
                                <td className="py-1.5 px-3 text-right font-mono text-sm">{cs}{row.base_spend?.toLocaleString()}</td>
                                <td className="py-1.5 px-3 text-right font-mono text-sm">{cs}{row.comp_spend?.toLocaleString()}</td>
                                <td className={cn('py-1.5 px-3 text-right font-mono text-sm', row.delta >= 0 ? 'text-destructive' : 'text-secondary')}>
                                  {row.delta >= 0 ? '+' : ''}{cs}{Math.abs(row.delta)?.toLocaleString()}
                                </td>
                                <td className="py-1.5 px-3 text-right"><DeltaBadge value={row.delta_pct} /></td>
                              </tr>
                            ))}</>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </TabsContent>

            {/* Entity Comparison */}
            <TabsContent value="entity">
              <div className="space-y-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card data-testid="business-unit-comparison-chart">
                    <CardHeader><CardTitle>Entity (Country Code) Spend: Base vs Comparison</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={data.by_entity || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="entity" stroke="hsl(var(--muted-foreground))" />
                          <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${cs}${(v / 1000).toFixed(0)}k`} />
                          <Tooltip contentStyle={tooltipStyle} formatter={(val) => [`${cs}${val?.toLocaleString()}`, '']} />
                          <Legend />
                          <Bar dataKey="base_spend" name={baseLabel} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="comp_spend" name={compLabel} fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} opacity={0.5} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <Card data-testid="business-unit-comparison-table">
                    <CardHeader><CardTitle>Entity (Country Code) Details</CardTitle></CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Entity (Country Code)</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Base Spend</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Comparison</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Delta</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            <>{(data.by_entity || []).map(row => (
                              <tr key={row.entity} className="border-b border-border hover:bg-muted/50 transition-colors">
                                <td className="py-1.5 px-3 font-medium text-sm">{row.entity}</td>
                                <td className="py-1.5 px-3 text-right font-mono text-sm">{cs}{row.base_spend?.toLocaleString()}</td>
                                <td className="py-1.5 px-3 text-right font-mono text-sm">{cs}{row.comp_spend?.toLocaleString()}</td>
                                <td className={cn('py-1.5 px-3 text-right font-mono text-sm', row.delta >= 0 ? 'text-destructive' : 'text-secondary')}>
                                  {row.delta >= 0 ? '+' : ''}{cs}{Math.abs(row.delta)?.toLocaleString()}
                                </td>
                                <td className="py-1.5 px-3 text-right"><DeltaBadge value={row.delta_pct} /></td>
                              </tr>
                            ))}</>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </TabsContent>

            {/* SBU Comparison */}
            <TabsContent value="sbu">
              <div className="space-y-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card data-testid="sbu-comparison-chart">
                    <CardHeader><CardTitle>SBU Spend: Base vs Comparison</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={data.by_sbu || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="sbu" stroke="hsl(var(--muted-foreground))" />
                          <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${cs}${(v / 1000).toFixed(0)}k`} />
                          <Tooltip contentStyle={tooltipStyle} formatter={(val) => [`${cs}${val?.toLocaleString()}`, '']} />
                          <Legend />
                          <Bar dataKey="base_spend" name={baseLabel} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="comp_spend" name={compLabel} fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} opacity={0.5} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <Card data-testid="sbu-comparison-table">
                    <CardHeader><CardTitle>SBU Details</CardTitle></CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">SBU</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Base Spend</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Comparison</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Delta</th>
                              <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            <>{(data.by_sbu || []).map(row => (
                              <tr key={row.sbu} className="border-b border-border hover:bg-muted/50 transition-colors">
                                <td className="py-1.5 px-3 font-medium text-sm">{row.sbu}</td>
                                <td className="py-1.5 px-3 text-right font-mono text-sm">{cs}{row.base_spend?.toLocaleString()}</td>
                                <td className="py-1.5 px-3 text-right font-mono text-sm">{cs}{row.comp_spend?.toLocaleString()}</td>
                                <td className={cn('py-1.5 px-3 text-right font-mono text-sm', row.delta >= 0 ? 'text-destructive' : 'text-secondary')}>
                                  {row.delta >= 0 ? '+' : ''}{cs}{Math.abs(row.delta)?.toLocaleString()}
                                </td>
                                <td className="py-1.5 px-3 text-right"><DeltaBadge value={row.delta_pct} /></td>
                              </tr>
                            ))}</>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </TabsContent>

            {/* Monthly Trend Overlay */}
            <TabsContent value="trend">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card data-testid="monthly-trend-chart">
                  <CardHeader><CardTitle>Monthly Spend Trend Overlay</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Base period monthly */}
                      <div>
                        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                          <Badge variant="default" className="text-xs">Base</Badge> {baseLabel}
                        </h3>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={data.base_monthly || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
                            <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${cs}${(v / 1000).toFixed(0)}k`} />
                            <Tooltip contentStyle={tooltipStyle} formatter={(val) => [`${cs}${val?.toLocaleString()}`, 'Spend']} />
                            <Bar dataKey="spend" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Comparison period monthly */}
                      <div>
                        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">Comparison</Badge> {compLabel}
                        </h3>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={data.comp_monthly || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 10 }} />
                            <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={v => `${cs}${(v / 1000).toFixed(0)}k`} />
                            <Tooltip contentStyle={tooltipStyle} formatter={(val) => [`${cs}${val?.toLocaleString()}`, 'Spend']} />
                            <Bar dataKey="spend" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]} opacity={0.6} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </TabsContent>
          </Tabs>
        </>
      )}
      <AIPanel page="comparative" filters={additionalFilters} />
    </div>
    </TrustScoreGate>
  );
}
